import React, { useContext, useEffect, useRef, useState } from "react";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";
import * as Yup from "yup";
import { JournalArtifactItems, SKJournalArtifact, ContentState } from "../../../models/SKArtifact";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigationBar } from "../../../context/NavigationContext";
import { EditPopupJornals } from "./EditPopupJornals";
import { AddStickyNote } from "./container/components/AddStickyNote";
import { AddTextAreaField } from "../../../components/AddTextAreaField";
import { useNavigate } from "react-router-dom";
import AudioInput from "./AudioInput";
import { AddVoiceNote } from "./container/components/AddVoiceNote";
import AudioPlayer from "../../../components/AudioPlayer";
import { FolderName } from "../../../api/shared/uploadService";
import { AddTextField } from "./container/components/AddTextField";

export default function AddJournalVoice() {
  const { contentData, artifactId, updateContentData, loading, saveData, isEditable } = useContext(CreateArtifactContext);
  const { setNavProps } = useNavigationBar();
  const [isValidForm, setIsValidForm] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | undefined>("");
  const [items, setItems] = useState<JournalArtifactItems[]>([]);
  const [itemTitle, setItemTitle] = useState("");
  var [isValidInput, setIsValidInput] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editItem, setEditItem] = useState<JournalArtifactItems>();

  const navigate = useNavigate();
  useEffect(() => {
    setNavProps(prev => ({
      ...prev,
      backArrow: false,
      buttonTitle: "Done",
      title: "Add Journal",
      getActionItems: undefined,
      enableLink: isEditable ? isValidForm : false,
      onNext: isEditable ? submit : undefined,
      onClose: () => navigate(-1),
      onEdit: undefined,
    }));
  }, [isValidForm, setNavProps, isEditable]);

  const schema = Yup.object().shape({
    journalItems: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          journalTitle: Yup.string().required(),
          journalVoiceURL: Yup.string().required(),
        })
      )
      .min(1, "At least one journal item is required")
      .required(),
  });

  useEffect(() => {
    const validate = async () => {
      try {
        await schema.validate(contentData, { abortEarly: false });
        setIsValidForm(true);
      } catch (err) {
        setIsValidForm(false);
      }
    };
    validate();
  }, [contentData, schema]);

  useEffect(() => {
    if ((contentData as SKJournalArtifact).journalItems != null) {
      setItems((contentData as SKJournalArtifact).journalItems);
    }
  }, []);

  useEffect(() => {
    if (audioUrl) {
      setIsValidInput(true);
    } else {
      setIsValidInput(false);
    }
  }, [audioUrl]);

  const handleRemoveItem = (id: string) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    updateContentData({ journalItems: updatedItems });
  };

  const getItemById = (id: string): JournalArtifactItems | undefined => {
    return items.find(item => item.id === id);
  };

  const editItemPopup = (id: string) => {
    setEditItem(getItemById(id));
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const savePopup = async (editNotes: string) => {
    if (editItem) {
      const updatedItem = {
        ...editItem,
        journalNote: editNotes,
      };
      const updatedItems = items.map(item => (item.id === editItem.id ? updatedItem : item));
      setItems(updatedItems);
      await updateContentData({ journalItems: updatedItems });
      closePopup();
    }
  };

  const submit = async () => {
    await saveData();
    window.history.back();
  };

  const handleSave = async () => {
    try {
      setIsValidInput(false);

      if (audioUrl) {
        const newItem: JournalArtifactItems = {
          id: new Date().toISOString(),
          journalTitle: itemTitle,
          journalVoiceURL: audioUrl,
        };
        const updatedItems = [...items, newItem];
        setItems(updatedItems);
        updateContentData({ journalItems: updatedItems });
        setAudioUrl(undefined);
        setItemTitle("");
      }
      setIsValidInput(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleAudioChange = (fileUrl: string | null) => {
    if (fileUrl) {
      setAudioUrl(fileUrl);
    }
  };

  return (
    <div className="flex flex-col mt-[12px] gap-[30px]">
      {isEditable && (
        <div className="flex flex-col gap-3">
          <AudioInput
            audioUrl={audioUrl}
            docId={artifactId!}
            onFileChange={fileUrl => {
              isEditable && handleAudioChange(fileUrl);
            }}
            editable={isEditable}
            removeAudio={() => setAudioUrl(undefined)}
            folder={FolderName.content}
            maxAudioDuration={5400} // 90 minutes
            maxFileSize={50} // 50 MB
          />

          <AddTextField
            placeholder={"Description"}
            charAllowed={100}
            value={itemTitle}
            onChange={async e => {
              setItemTitle(e.target.value);
            }}
            disabled={contentData.status !== ContentState.IN_REVIEW ? false : true}
          />
          {/* <AddTextAreaField
            name={"AddNote"}
            title={"Add Note"}
            note={"Type or paste the text."}
            placeholder={"Note"}
            index={1}
            height={150}
            charAllowed={150}
            value={itemNotes}
            onChange={async e => {
              setItemNotes(e.target.value);
            }}
            disabled={contentData.status !== ContentState.IN_REVIEW ? false : true}
          /> */}
          <div>
            <button
              onClick={handleSave}
              className={
                (audioUrl && itemTitle ? "bg-black cursor-pointer" : "bg-gray-400 disabled-link") +
                " flex items-center justify-center px-3 py-1 text-sm font-normal text-white rounded-lg flex-shrink-0 w-[68px] h-[32px] mr-auto"
              }
            >
              Add
            </button>
          </div>
        </div>
      )}
      <div>
        <div className="flex items-center">
          {isEditable && <span className="content_count">2</span>}
          <span className="content_title">Audio Added</span>
        </div>
        <div className="notes-class-content">Audio files you add show up here, you can edit, reorder, and delete.</div>
        <div className="flex flex-wrap gap-[15px]">
          {items.map((note, index) => (
            <div key={index} className="relative flex flex-col w-full md:max-w-[300px] py-7">
              <AudioPlayer src={note.journalVoiceURL!} onClose={isEditable ? () => handleRemoveItem(note.id) : undefined} />
              <span className="absolute text-sm font-semibold top-2 left-2">{note.journalTitle}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
